/**
 * Cache Settings Service
 * Manages cache enabled/disabled state via localStorage
 */

import { LocalStorageItem } from '../../common/constants'

/**
 * Singleton service to manage cache enabled/disabled state
 */
class CacheSettingsService {
  private readonly CACHE_ENABLED_KEY = LocalStorageItem.cacheEnabled
  private readonly ENCRYPTION_ENABLED_KEY = LocalStorageItem.encryptionEnabled

  /**
   * Check if cache is enabled (default: false)
   */
  isCacheEnabled(): boolean {
    const value = localStorage.getItem(this.CACHE_ENABLED_KEY)
    // Default to false if not set
    return value === null ? false : value === 'true'
  }

  /**
   * Update cache setting
   * @param enabled - Whether cache should be enabled
   */
  setCacheEnabled(enabled: boolean): void {
    localStorage.setItem(this.CACHE_ENABLED_KEY, String(enabled))
    console.log(`🔧 Cache ${enabled ? 'enabled' : 'disabled'}`)
  }

  /**
   * Get current setting (alias for isCacheEnabled)
   */
  getCacheEnabled(): boolean {
    return this.isCacheEnabled()
  }

  /**
   * Check if encryption is enabled (default: true)
   */
  isEncryptionEnabled(): boolean {
    const value = localStorage.getItem(this.ENCRYPTION_ENABLED_KEY)
    // Default to true if not set
    return value === null ? true : value === 'true'
  }

  /**
   * Update encryption setting
   * @param enabled - Whether encryption should be enabled
   */
  setEncryptionEnabled(enabled: boolean): void {
    localStorage.setItem(this.ENCRYPTION_ENABLED_KEY, String(enabled))
    console.log(`🔒 Cache encryption ${enabled ? 'enabled' : 'disabled'}`)
  }

  /**
   * Get current encryption setting
   */
  getEncryptionEnabled(): boolean {
    return this.isEncryptionEnabled()
  }
}

// Export singleton instance
export const cacheSettingsService = new CacheSettingsService()
